import IComponent, {ComponentContext, ComponentLifecycle} from './IComponent';
import { Naja } from 'naja';
import IExtension from '../extensions/IExtension';

export type ComponentsType = {[componentName: string]: new ($component: JQuery<HTMLElement>, context?: any) => IComponent};

// This factory is naja extension
export class ComponentFactory implements IExtension, ComponentLifecycle {

    private readonly context: ComponentContext = {components: this};

    constructor (
        private readonly components: ComponentsType
    ) {
        this.components = components;
    }

    initialize (naja: Naja): void {
        this.context.naja = naja;

        if (process.env.NODE_ENV === 'development') {
            console.log(`Registered components (${Object.keys(this.components).length})`, this.components);
        }

        this.initComponents($('body'));

        naja.snippetHandler.addEventListener('beforeUpdate', event => {
            const id = event.detail.snippet.id;
            const oldSnippet = id ? document.getElementById(event.detail.snippet.id) : null;
            if (oldSnippet) {
                $(oldSnippet).find('[data-component-initialized]').each((i, componentEl) => {
                    componentEl['echonityComponent']?.beforeRemove?.();
                })
            }
        });

        naja.snippetHandler.addEventListener('afterUpdate', event => {
            this.initComponents($(event.detail.snippet));

            // FIXME Move this code to extension or own component
            if (event.detail.snippet.id === 'snippet-discussion-newComment') {
                // refresh OpenAI - if exists
                if (this.components.OpenAI) {
                    this.initComponent($('[data-type="minutes-ai"]'));
                }
            }
        });
    }

    public mount ($root: JQuery<HTMLElement|Element>): void {
        $root.find('[data-component]').add($root.filter('[data-component]')).each((i, componentEl) => {
            if (!componentEl.hasAttribute('data-component-initialized') && !componentEl['echonityComponent']) {
                this.initComponent(componentEl, true);
            }
        });
    }

    public unmount ($root: JQuery<HTMLElement|Element>): void {
        const elements = $root.find('[data-component-initialized]')
            .add($root.filter('[data-component-initialized]')).toArray().reverse();
        elements.forEach(componentEl => {
            const component = componentEl['echonityComponent'];
            const name = componentEl.getAttribute('data-component-initialized');
            delete componentEl['echonityComponent'];
            componentEl.removeAttribute('data-component-initialized');
            try {
                component?.beforeRemove?.();
            } catch (err) {
                console.error(`Component "${name}" failed to clean up.\n`, err);
            }
        });
    }

    private initComponents ($root: JQuery<HTMLElement|Element>) {
        const $components = $root.find('[data-component]');
        if ($root.is('[data-component]')) {
            $components.add(<JQuery<HTMLElement>>$root);
        }
        $.each($components, (i, componentEl) => {
            this.initComponent(componentEl);
        });
    }

    private initComponent (componentEl, cleanupOnFailure = false) {
        const $component = $(componentEl);
        const name = $component.data('component');

        if (name && this.components[name]) {
            // JS component was found, try to initialize, skip on error
            let component: IComponent;
            try {
                component = new this.components[name]($component, this.context);

                component.init();

                ComponentFactory.success(name, componentEl);

                $component.removeAttr('data-component');
                $component.attr('data-component-initialized', name);
                $component.get(0).echonityComponent = component;

            } catch (err) {
                if (cleanupOnFailure) {
                    try {
                        (component || componentEl['echonityComponent'])?.beforeRemove?.();
                    } catch (cleanupError) {
                        console.error(`Component "${name}" failed to clean up.\n`, cleanupError);
                    }
                    delete componentEl['echonityComponent'];
                }
                console.error(`Component "${name}" failed to initialize due error. Skipped.\n`, err);
            }
        } else {
            // JS component is missing
            ComponentFactory.warn(name, componentEl);
        }
    }

    private static warn (name, componentEl): void {
        if (process.env.NODE_ENV !== 'production') {
            console.warn(`Component '${name}' does not exist. Did you register the component for the element 👇 ?`);
            console.log(componentEl);
        }
    }

    private static success (name, componentEl): void {
        if (process.env.NODE_ENV !== 'production') {
            console.log(`✅ ${name}`, componentEl);
        }
    }
}
